import { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { submitFeedback } from '../../api/feedback';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  sessionId: string;
}

export function FeedbackModal({ isOpen, onClose, sessionId }: FeedbackModalProps) {
  const [text, setText] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleClose = useCallback(() => {
    setText('');
    setSubmitted(false);
    onClose();
  }, [onClose]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleClose]);

  // Auto-close after submission
  useEffect(() => {
    if (!submitted) return;
    const timer = setTimeout(handleClose, 1500);
    return () => clearTimeout(timer);
  }, [submitted, handleClose]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!text.trim()) return;
    submitFeedback({
      feedback_type: 'free_form',
      session_id: sessionId,
      feedback_text: text.trim(),
    });
    setSubmitted(true);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        {submitted ? (
          <div className="text-center py-8">
            <p className="text-lg font-semibold text-gray-900">Thank you for your feedback!</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Share Feedback</h2>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <textarea
              className="w-full h-32 px-4 py-3 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              placeholder="What's on your mind? Share any feedback about Hudl Coach..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              autoFocus
            />
            <div className="flex justify-end mt-4">
              <button
                onClick={handleSubmit}
                disabled={!text.trim()}
                className="px-6 py-2 rounded-lg text-white font-medium text-sm bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                Submit
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
